import React, { useEffect, useRef, useState } from 'react';
import { func, object } from 'prop-types';

import { FormattedMessage } from '../../../util/reactIntl';
import { getTurnstileConfig } from '../../../util/api';

import css from './SignupSecurity.module.css';

const TURNSTILE_SCRIPT_ID = 'cloudflare-turnstile-script';
const TURNSTILE_SCRIPT_URL =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TURNSTILE_ACTION = 'turnstile-spin-v1';

let scriptPromise;

export const loadTurnstileScript = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('Turnstile requires a browser.'));
  }

  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }

  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID);
    const script = existingScript || document.createElement('script');
    const timeoutId = window.setTimeout(() => {
      scriptPromise = null;
      reject(new Error('Turnstile script timed out.'));
    }, 15000);

    const handleLoad = () => {
      window.clearTimeout(timeoutId);
      if (window.turnstile) {
        resolve(window.turnstile);
      } else {
        scriptPromise = null;
        reject(new Error('Turnstile did not initialize.'));
      }
    };
    const handleError = () => {
      window.clearTimeout(timeoutId);
      scriptPromise = null;
      reject(new Error('Turnstile script failed to load.'));
    };

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });

    if (!existingScript) {
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = TURNSTILE_SCRIPT_URL;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });

  return scriptPromise;
};

const Turnstile = props => {
  const { onTokenChange, resetKey } = props;
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenChangeRef = useRef(onTokenChange);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    onTokenChangeRef.current = onTokenChange;
  }, [onTokenChange]);

  useEffect(() => {
    let isMounted = true;

    Promise.all([getTurnstileConfig(), loadTurnstileScript()])
      .then(([config, turnstile]) => {
        if (!isMounted || !containerRef.current) {
          return;
        }

        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: config.siteKey,
          action: TURNSTILE_ACTION,
          appearance: 'always',
          size: 'flexible',
          retry: 'auto',
          'retry-interval': 8000,
          'refresh-expired': 'auto',
          'refresh-timeout': 'auto',
          'response-field': false,
          callback: token => {
            if (isMounted) {
              setStatus('verified');
              onTokenChangeRef.current(token);
            }
          },
          'expired-callback': () => {
            if (isMounted) {
              setStatus('expired');
              onTokenChangeRef.current('');
            }
          },
          'timeout-callback': () => {
            if (isMounted) {
              setStatus('timeout');
              onTokenChangeRef.current('');
            }
          },
          'error-callback': () => {
            if (isMounted) {
              setStatus('error');
              onTokenChangeRef.current('');
            }
            return false;
          },
        });
        setStatus('ready');
      })
      .catch(() => {
        if (isMounted) {
          setStatus('error');
          onTokenChangeRef.current('');
        }
      });

    return () => {
      isMounted = false;
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (resetKey && window.turnstile && widgetIdRef.current !== null) {
      window.turnstile.reset(widgetIdRef.current);
      setStatus('ready');
      onTokenChangeRef.current('');
    }
  }, [resetKey]);

  const errorStatus = ['error', 'expired', 'timeout'].includes(status);
  const statusMessageId =
    status === 'expired'
      ? 'SignupForm.turnstileExpired'
      : status === 'timeout'
      ? 'SignupForm.turnstileTimeout'
      : 'SignupForm.turnstileError';

  return (
    <div className={css.turnstileWrapper}>
      <div
        ref={containerRef}
        className={css.turnstile}
        data-action={TURNSTILE_ACTION}
        data-testid="turnstile-container"
      />
      {status === 'loading' ? (
        <p className={css.securityStatus} role="status">
          <FormattedMessage id="SignupForm.turnstileLoading" />
        </p>
      ) : null}
      {errorStatus ? (
        <p className={css.securityError} role="alert">
          <FormattedMessage id={statusMessageId} />
        </p>
      ) : null}
    </div>
  );
};

Turnstile.defaultProps = {
  resetKey: null,
};

Turnstile.propTypes = {
  onTokenChange: func.isRequired,
  resetKey: object,
};

export default Turnstile;
